// apps/nos/client/src/hooks/useFlavorSession.ts
import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchFlavorSession, type HydratedSession } from "@/services/flavor_api";
import { useSessionStore } from "@/stores/sessionStore";
import { SAMPLE_TARGET } from "@/services/mock";
import type { Session } from "@/utils/types/flavor";
import { buildSessionConstraints } from "@/utils/formulation/withSessionConstraints";
import { flavorKeys } from "./useSidebarFlavors";

// Maps the server's HydratedSession payload onto the client Session shape.
// Today the server returns just identity fields; `target` is still stubbed
// from SAMPLE_TARGET (composition for the aside, plots, etc.) — to be
// replaced when the server emits real target data. `formulation` is left
// empty here so the workbench doesn't paint mock rows while the real
// formulation is being fetched by useFlavorFormulas → useFormulationVariants.
function toSession(payload: HydratedSession): Session {
  const stubSession: Session = {
    id: payload.id,
    label: payload.label,
    name: payload.name,
    starred: payload.isStarred,
  };
  return {
    ...stubSession,
    target: {
      ...SAMPLE_TARGET,
      constraints: buildSessionConstraints(stubSession),
    },
    formulation: [],
  };
}

export function useFlavorSession(id: string | null) {
  const setSession = useSessionStore((s) => s.setSession);

  const q = useQuery({
    queryKey: flavorKeys.byId(id ?? "__none__"),
    queryFn: () => fetchFlavorSession(id!),
    enabled: id !== null,
    staleTime: 30_000,
  });

  useEffect(() => {
    if (q.data) setSession(toSession(q.data));
  }, [q.data, setSession]);

  return q;
}
